import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ToursV3Service } from 'api-v3/services';
import { CarrierToursArrayV3, SortType } from 'api-v3/models';
import { ToursStore } from '@app/stores/toursStore';
import { TourData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import {
    getFullTextSearchParam,
    withColumnFilters,
} from '@app/shared/utils/paginated-search';

@Injectable({
    providedIn: 'root',
})
export class CarrierToursPaginatedService {
    private toursStore = inject(ToursStore);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);

    constructor(private toursV3Service: ToursV3Service) {}

    async loadCarrierToursPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        this.toursStore.setToursLoading(true);
        this.toursStore.setToursError(null);

        try {
            const searchText = this.searchStore.searchText();
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const columnFilters = this.searchStore.columnFilters();

            // Get selected locations and showClosed filter
            const selectedSiteKeys = this.appStore.getSelectedLocationKeys('carrier');
            const showClosed = this.appStore.showClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            const sortBy = sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // One query param per active column filter, AND-ed with each other
            // and with the full-text search by the backend.
            const params = withColumnFilters(
                {
                    organizationSiteKey,
                    organizationSiteKeys:
                        selectedSiteKeys.length > 0
                            ? selectedSiteKeys
                            : undefined,
                    limit: pagination.limit,
                    offset: pagination.offset,
                    sort: sortDirection,
                    sortBy: validSortBy,
                    filterNotClosed: !showClosed,
                    search: getFullTextSearchParam(searchText),
                },
                columnFilters
            );

            // This will go through the HTTP interceptors
            const response = await lastValueFrom(
                this.toursV3Service.getCarrierToursV3(params)
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const toursWithOrgSiteKey: TourData[] = response.data.map(
                    tour => ({
                        ...tour,
                        orgSiteKey: organizationSiteKey,
                    })
                );

                this.toursStore.setTours(toursWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_TOURS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_TOURS';

            this.toursStore.clearTours();
            this.toursStore.setToursError(errorMessage);

            // Reset pagination to empty state on error
            this.searchStore.resetPaginationToEmpty();
        } finally {
            this.toursStore.setToursLoading(false);
        }
    }
}
